import * as React from "react";

import { ControlElement, LayoutProps, Resolve, UISchemaElement } from "@jsonforms/core";
import { JsonFormsDispatch, JsonFormsStateContext, withJsonFormsContext } from "@jsonforms/react";

import Layout from "./Layout";

interface DispatchPropsOfCardRenderer {
	onRemove(): () => void;
	onMoveUp(): () => void;
	onMoveDown(): () => void;
	onMoveToTop(): () => void;
	onMoveToBottom(): () => void;
}

interface CardRendererProps extends LayoutProps, DispatchPropsOfCardRenderer {
	index: number;
	data: number;
	moveCard: (from: number, to: number) => void;
	setDragFocusIndex: React.Dispatch<React.SetStateAction<number | undefined>>;
	isDraggingSelected: boolean;
	setIsDraggingSelected: React.Dispatch<React.SetStateAction<boolean>>;
	setAnnouncement: React.Dispatch<React.SetStateAction<string>>;
	onCopyArrayValues: (index: number) => void;
	onPasteArrayValues: (index: number) => void;
	canPaste: boolean;
	dragFocusIndex?: number;
	label?: string;
	removable?: boolean;
	isFocused?: boolean;
	headingLevel?: number;
	showAccrordion?: boolean;
	isChildArray?: boolean;
}

const controlWithoutLabel = (scope: string): ControlElement => ({
	type: "Control",
	scope: scope,
	label: false,
});

export const CardRenderer: React.FC<CardRendererProps> = ({
	uischema,
	schema,
	path,
	renderers,
	cells,
	onRemove,
	index,
	label,
	enabled,
	data,
	onMoveDown,
	onMoveUp,
	onMoveToTop,
	onMoveToBottom,
	removable,
	isFocused,
	headingLevel,
	moveCard,
	setDragFocusIndex,
	dragFocusIndex,
	isDraggingSelected,
	setIsDraggingSelected,
	setAnnouncement,
	onCopyArrayValues,
	onPasteArrayValues,
	canPaste,
	showAccrordion = false,
	isChildArray = false,
}) => {
	const [showMenu, setShowMenu] = React.useState<boolean>(false);

	const elements = uischema.options?.["detail"] ? uischema.options?.["detail"]["elements"] : [];

	const itemsToRender: React.JSX.Element =
		elements?.length > 0 ? (
			elements.map((element: UISchemaElement | undefined, i: number) => (
				<JsonFormsDispatch
					schema={schema}
					uischema={element}
					path={path}
					enabled={enabled}
					renderers={renderers}
					cells={cells}
					key={i}
				/>
			))
		) : (
			<JsonFormsDispatch
				schema={schema}
				uischema={controlWithoutLabel("#")}
				path={path}
				enabled={enabled}
				renderers={renderers}
				cells={cells}
				key={index}
			/>
		);

	const panelLabel = label || `Item ${index + 1}`;
	const panelAriaLabel = `Item ${index + 1} of ${data}: ${label}` || `Item ${index + 1} of ${data}`;

	const handleKeyUp: React.KeyboardEventHandler<HTMLButtonElement> = React.useCallback(
		(e) => {
			const key = e.key;

			if (key === " " || key === "Enter") {
				e.preventDefault(); // block default button behavior
				e.stopPropagation(); // avoid bubbling to parent
				if (isDraggingSelected) {
					// DROP
					setAnnouncement(`${panelLabel} dropped.`);
					setIsDraggingSelected(false);
					setDragFocusIndex(undefined);
				} else {
					// GRAB
					setIsDraggingSelected(true);
					setDragFocusIndex(index);
					setAnnouncement(`${panelLabel} grabbed. Use arrow keys to move, space or enter to drop.`);
				}
				return;
			}
		},
		[index, isDraggingSelected, panelLabel, setAnnouncement, setDragFocusIndex, setIsDraggingSelected]
	);

	const handleOnKeyPressed = React.useCallback(
		(e: React.KeyboardEvent<HTMLButtonElement>) => {
			const key = e.key;

			if (!isDraggingSelected) {
				return;
			}
			if (key === "Tab" && isDraggingSelected) {
				e.preventDefault();
				e.stopPropagation();
				return;
			}

			if (key === "ArrowUp" || key === "ArrowDown" || key === "Home" || key === "End" || key === "Escape") {
				e.preventDefault();
				e.stopPropagation();

				if (key === "ArrowUp" && index > 0) {
					onMoveUp();
					setDragFocusIndex(index - 1);
					setAnnouncement(`${panelLabel} moved up. Now at position ${index} of ${data}.`);
				} else if (key === "ArrowDown" && index < data - 1) {
					onMoveDown();
					setDragFocusIndex(index + 1);
					setAnnouncement(`${panelLabel} moved down. Now at position ${index + 2} of ${data}.`);
				} else if (key === "Home" && index !== 0) {
					onMoveToTop();
					setDragFocusIndex(0);
					setAnnouncement(`${panelLabel} moved to the top. Position 1 of ${data}.`);
				} else if (key === "End" && index !== data - 1) {
					onMoveToBottom();
					setDragFocusIndex(data - 1);
					setAnnouncement(`${panelLabel} moved to the bottom. Position ${data} of ${data}.`);
				} else if (key === "Escape") {
					setAnnouncement("Drag cancelled.");
					setIsDraggingSelected(false);
					setDragFocusIndex(undefined);
				}
			}
		},
		[
			data,
			index,
			isDraggingSelected,
			onMoveDown,
			onMoveToBottom,
			onMoveToTop,
			onMoveUp,
			panelLabel,
			setAnnouncement,
			setDragFocusIndex,
			setIsDraggingSelected,
		]
	);

	React.useEffect(() => {
		if (!showMenu) return;

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				e.stopPropagation();
				e.preventDefault();
				setShowMenu(false);
			}
		};

		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [showMenu]);

	return (
		<Layout
			isFocused={isFocused}
			index={index}
			dragFocusIndex={dragFocusIndex}
			data={data}
			moveCard={moveCard}
			onMoveDown={onMoveDown}
			onMoveUp={onMoveUp}
			onMoveToBottom={onMoveToBottom}
			onMoveToTop={onMoveToTop}
			onRemove={onRemove}
			showMenu={showMenu}
			panelLabel={panelLabel}
			panelAriaLabel={panelAriaLabel}
			isDraggingSelected={isDraggingSelected}
			setShowMenu={setShowMenu}
			handleOnKeyPressed={handleOnKeyPressed}
			handleKeyUp={handleKeyUp}
			headingLevel={headingLevel}
			itemsToRender={itemsToRender}
			enabled={enabled}
			path={path}
			removable={removable}
			showAccrordion={showAccrordion}
			isChildArray={isChildArray}
			noBorder={elements.length === 0}
			onCopyArrayValues={onCopyArrayValues}
			onPasteArrayValues={onPasteArrayValues}
			canPaste={canPaste}
		/>
	);
};

const cardRenderer = React.memo(CardRenderer);

const withContextToCardRendered = (
	Component: React.ComponentType<CardRendererProps>
): React.ComponentType<CardRendererProps> => {
	return ({ ctx, props }: JsonFormsStateContext & CardRendererProps) => {
		const childData = Resolve.data(ctx.core.data, props.path);
		const label: string | undefined =
			childData?.[props.uischema?.options?.elementLabelProp] || childData?.[Object.keys(childData)[0]] || undefined;
		return <Component {...props} label={typeof label === "string" ? label : `Item-${props.index + 1}`} />;
	};
};

const withCustomProps = (Component: React.ComponentType<CardRendererProps>) => {
	return withJsonFormsContext(withContextToCardRendered(Component));
};

export default withCustomProps(cardRenderer);
